import { api, createApi } from '../api';
import type {
  Annotation,
  Connection,
  CreateTaskPayload,
  Cursor,
  Id,
  TaskGroup,
  TaskInteractionResponse,
  TaskStateDetail,
  Visitor,
  WorkflowGraphTask,
  WorkflowMetadata,
  WorkflowSummary,
  WorkflowWebhook,
  WorkflowWithData,
} from '../api/types';
import { environment } from '../config/environment';

function getClient(baseUrl?: string) {
  return baseUrl ? createApi({ baseUrl }).workflows : api.workflows;
}

export interface WorkflowData {
  workflow: WorkflowMetadata;
  tasks: WorkflowGraphTask[];
  connections: Connection[];
  taskGroups: TaskGroup[];
  annotations?: Annotation[] | null;
}

export class WorkflowService {
  private resolveBaseUrl(baseUrl?: string): string {
    return baseUrl ?? environment.apiBaseUrl;
  }

  private join(baseUrl: string, path: string): string {
    if (!baseUrl.endsWith('/') && !path.startsWith('/')) {
      return `${baseUrl}/${path}`;
    }
    if (baseUrl.endsWith('/') && path.startsWith('/')) {
      return `${baseUrl}${path.slice(1)}`;
    }
    return `${baseUrl}${path}`;
  }

  list(baseUrl?: string): Promise<WorkflowSummary[]> {
    return getClient(baseUrl).list();
  }

  create(baseUrl?: string): Promise<WorkflowMetadata> {
    return getClient(baseUrl).create();
  }

  remove(id: Id, baseUrl?: string) {
    return getClient(baseUrl).remove(id);
  }

  get(id: Id, baseUrl?: string): Promise<WorkflowMetadata> {
    return getClient(baseUrl).get(id);
  }

  getTaskState(
    workflowId: Id,
    taskId: Id,
    baseUrl?: string
  ): Promise<TaskStateDetail> {
    return getClient(baseUrl).getTaskState(workflowId, taskId);
  }

  async fetchWorkflowData(id: Id, baseUrl?: string): Promise<WorkflowData> {
    const client = getClient(baseUrl);
    const [workflow, tasks, connections, taskGroups] = await Promise.all([
      client.get(id),
      client.listTasks(id),
      client.listConnections(id),
      client.listTaskGroups(id).catch(() => []),
    ]);
    return {
      workflow,
      tasks,
      connections,
      taskGroups,
      annotations: workflow.annotations,
    };
  }

  run(id: Id, baseUrl?: string) {
    return getClient(baseUrl).run(id);
  }

  reset(id: Id, baseUrl?: string) {
    return getClient(baseUrl).reset(id);
  }

  createTask(workflowId: Id, payload: CreateTaskPayload, baseUrl?: string) {
    return getClient(baseUrl).createTask(workflowId, payload);
  }

  respondToTaskInteraction(
    workflowId: Id,
    taskId: Id,
    payload: TaskInteractionResponse,
    baseUrl?: string
  ) {
    return getClient(baseUrl).respondToTaskInteraction(
      workflowId,
      taskId,
      payload
    );
  }

  uploadTaskInteractionFile(
    workflowId: Id,
    taskId: Id,
    payload: { file: File; response?: TaskInteractionResponse },
    baseUrl?: string
  ) {
    return getClient(baseUrl).uploadTaskInteractionFile(
      workflowId,
      taskId,
      payload
    );
  }

  createVisitor(workflowId: Id, visitor: Visitor, baseUrl?: string) {
    return getClient(baseUrl).createVisitor(workflowId, visitor);
  }

  updateVisitorCursor(
    workflowId: Id,
    visitorId: string,
    cursor: Cursor,
    baseUrl?: string
  ) {
    return getClient(baseUrl).updateVisitorCursor(
      workflowId,
      visitorId,
      cursor
    );
  }

  listWebhooks(workflowId: Id, baseUrl?: string): Promise<WorkflowWebhook[]> {
    return getClient(baseUrl).listWebhooks(workflowId);
  }

  createWebhook(workflowId: Id, baseUrl?: string): Promise<WorkflowWebhook> {
    return getClient(baseUrl).createWebhook(workflowId);
  }

  deleteWebhook(
    workflowId: Id,
    webhookId: string,
    baseUrl?: string
  ): Promise<{ message: string } | undefined> {
    return getClient(baseUrl).deleteWebhook(workflowId, webhookId);
  }

  deleteTaskGroup(
    workflowId: Id,
    taskGroupId: Id,
    baseUrl?: string
  ): Promise<{ message: string } | undefined> {
    return getClient(baseUrl).deleteTaskGroup(workflowId, taskGroupId);
  }

  createTaskGroup(
    workflowId: Id,
    payload: {
      title: string;
      taskIds?: Id[];
      childGroupIds?: Id[];
    },
    baseUrl?: string
  ): Promise<TaskGroup> {
    return getClient(baseUrl).createTaskGroup(workflowId, payload);
  }

  updateTaskGroup(
    workflowId: Id,
    taskGroupId: Id,
    payload: {
      title?: string;
      taskIds?: Id[];
      childGroupIds?: Id[];
    },
    baseUrl?: string
  ): Promise<TaskGroup> {
    return getClient(baseUrl).updateTaskGroup(workflowId, taskGroupId, payload);
  }

  getWebhookUrl(
    webhook: string | Pick<WorkflowWebhook, 'path'>,
    baseUrl?: string
  ): string {
    const path = typeof webhook === 'string' ? webhook : webhook.path;
    return this.join(this.resolveBaseUrl(baseUrl), path);
  }

  async export(
    workflowId: Id,
    baseUrl?: string
  ): Promise<{ blob: Blob; filename?: string }> {
    const url = this.join(
      this.resolveBaseUrl(baseUrl),
      `/workflows/${workflowId}/export`
    );
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) {
      const message = await res.text();
      throw new Error(message || 'Failed to export automation');
    }

    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition');
    const filename = disposition
      ? this.parseContentDispositionFilename(disposition)
      : undefined;
    return { blob, filename };
  }

  private parseContentDispositionFilename(
    disposition: string
  ): string | undefined {
    const quoted = disposition.match(/filename\*?=(?:UTF-8'')?"([^"]*)"/i);
    if (quoted?.[1]) return quoted[1].trim();
    const unquoted = disposition.match(/filename\*?=(?:UTF-8'')?([^"';]\S*)/i);
    return unquoted?.[1]?.trim();
  }

  async import(file: Blob, baseUrl?: string): Promise<WorkflowWithData> {
    const url = this.join(this.resolveBaseUrl(baseUrl), `/workflows/import`);
    const form = new FormData();
    form.append('file', file);

    const res = await fetch(url, {
      method: 'POST',
      body: form,
      credentials: 'include',
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || 'Failed to import automation');
    }

    return (await res.json()) as WorkflowWithData;
  }
}

export const workflowService = new WorkflowService();
